import { useCallback } from "react";
import {
  fx_FB_SendAssociateRequest,
  fx_FB_CancelAssociateRequest,
  fx_FB_AcceptAssociateRequest,
  fx_FB_DeclineAssociateRequest,
  fx_FB_FireAssociate,
} from "../functions/Fx_AssociateFunctions";

export interface AssociateTarget {
  uid: string;
  firstName: string;
  lastName: string;
  company: string;
  profilePicURL: string;
}

export interface UseAssociateRequestHandlersProps {
  profile: {
    UserId: string;
    FirstName: string;
    LastName: string;
    Company: string;
    ProfilePicURL: string;
  };
  presentAlert: (options: {
    header: string;
    message?: string;
    buttons: Array<string | { text: string; handler: () => void }>;
  }) => void;
  customMessages?: {
    sendRequest?: (target: AssociateTarget) => { header: string; message?: string };
    cancelRequest?: { header: string; message?: string };
    acceptRequest?: (target: AssociateTarget) => { header: string; message?: string };
    declineRequest?: { header: string; message?: string };
    fireAssociate?: { header: string; message?: string };
  };
}

/**
 * Hook for managing associate request action handlers
 * Used across mobile/desktop interfaces for consistent behavior
 * 
 * Provides handlers for:
 * - Sending associate requests
 * - Canceling outgoing requests
 * - Accepting incoming requests
 * - Declining incoming requests
 * - Removing existing associates (fire/drop)
 */
export const useAssociateRequestHandlers = ({
  profile,
  presentAlert,
  customMessages = {},
}: UseAssociateRequestHandlersProps) => {
  
  const handleSendRequest = useCallback((
    targetUid: string,
    targetFirstName: string,
    targetLastName: string,
    targetCompany: string,
    targetProfilePicURL: string
  ) => {
    const target: AssociateTarget = {
      uid: targetUid,
      firstName: targetFirstName,
      lastName: targetLastName,
      company: targetCompany,
      profilePicURL: targetProfilePicURL,
    };

    const sendTheRequest = () => {
      fx_FB_SendAssociateRequest(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company,
        profile.ProfilePicURL,
        target.uid,
        target.firstName,
        target.lastName,
        target.company,
        target.profilePicURL
      );
    };

    const messages = customMessages.sendRequest?.(target) || {
      header: "Send Request?",
      message: undefined,
    };

    presentAlert({
      ...messages,
      buttons: ["Cancel", { text: "Ok", handler: sendTheRequest }],
    });
  }, [profile, presentAlert, customMessages]);

  const handleCancelRequest = useCallback((
    targetUid: string,
    targetFirstName: string,
    targetLastName: string,
    targetCompany: string,
    targetProfilePicURL: string
  ) => {
    const cancelTheRequest = () => {
      fx_FB_CancelAssociateRequest(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company,
        profile.ProfilePicURL,
        targetUid,
        targetFirstName,
        targetLastName,
        targetCompany,
        targetProfilePicURL
      );
    };

    const messages = customMessages.cancelRequest || {
      header: "Cancel Request?",
      message: "Are you sure you want to cancel this request?",
    };

    presentAlert({
      ...messages,
      buttons: ["Cancel", { text: "Ok", handler: cancelTheRequest }],
    });
  }, [profile, presentAlert, customMessages]);

  const handleAcceptRequest = useCallback((
    targetUid: string,
    targetFirstName: string,
    targetLastName: string,
    targetCompany: string,
    targetProfilePicURL: string
  ) => {
    const target: AssociateTarget = {
      uid: targetUid,
      firstName: targetFirstName,
      lastName: targetLastName,
      company: targetCompany,
      profilePicURL: targetProfilePicURL,
    };

    const acceptTheRequest = async () => {
      fx_FB_AcceptAssociateRequest(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company,
        profile.ProfilePicURL,
        target.uid,
        target.firstName,
        target.lastName,
        target.company,
        target.profilePicURL
      );
    };

    const messages = customMessages.acceptRequest?.(target) || {
      header: "Accept Request?",
      message: undefined,
    };

    presentAlert({
      ...messages,
      buttons: ["Cancel", { text: "Accept", handler: acceptTheRequest }],
    });
  }, [profile, presentAlert, customMessages]);

  const handleDeclineRequest = useCallback((
    targetUid: string,
    targetFirstName: string,
    targetLastName: string,
    targetCompany: string,
    targetProfilePicURL: string
  ) => {
    const declineTheRequest = async () => {
      fx_FB_DeclineAssociateRequest(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company,
        profile.ProfilePicURL,
        targetUid,
        targetFirstName,
        targetLastName,
        targetCompany,
        targetProfilePicURL
      );
    };

    const messages = customMessages.declineRequest || {
      header: "Decline Request?",
      message: "Are you sure you want to decline this request?",
    };

    presentAlert({
      ...messages,
      buttons: ["Cancel", { text: "Decline", handler: declineTheRequest }],
    });
  }, [profile, presentAlert, customMessages]);

  const handleFireAssociate = useCallback((
    targetUid: string,
    targetFirstName: string,
    targetLastName: string,
    targetCompany: string,
    targetProfilePicURL: string
  ) => {
    const fireTheAssociate = () => {
      fx_FB_FireAssociate(
        profile.UserId,
        profile.FirstName,
        profile.LastName,
        profile.Company,
        profile.ProfilePicURL,
        targetUid,
        targetFirstName,
        targetLastName,
        targetCompany,
        targetProfilePicURL
      );
    };

    const messages = customMessages.fireAssociate || {
      header: "Drop Associate?",
      message: undefined,
    };

    presentAlert({
      ...messages,
      buttons: ["Cancel", { text: "Ok", handler: fireTheAssociate }],
    });
  }, [profile, presentAlert, customMessages]);

  return {
    handleSendRequest,
    handleCancelRequest,
    handleAcceptRequest,
    handleDeclineRequest,
    handleFireAssociate,
  };
};
